import { useCallback, useEffect, useSyncExternalStore } from 'react'

// The sidebar's "Staff tools" switch (option D, owner 2026-10-07).
//
// Off by default. The person's own choice is remembered in this browser
// (localStorage); every access is guarded, so a private window or blocked
// storage still works, for the life of the tab. A staff page (/admin/...,
// /root/...) turns it on by itself -- so a link from an alert email never
// lands on a page whose menu is hidden -- and that automatic "on" is never
// saved as the choice: it lasts until the person flips the switch or the tab
// is reloaded.
//
// The desktop column and the phone drawer each mount a SidebarLeft, so the
// tab's state lives here, once, and both read it.

export const STAFF_TOOLS_KEY = 'edxr.sidebar.staffTools'

export const isStaffRoute = pathname => /^\/(admin|root)(\/|$)/.test(String(pathname || ''))

const readSaved = () => {
  try {
    return window.localStorage.getItem(STAFF_TOOLS_KEY) === 'on'
  } catch (e) {
    return false
  }
}

const writeSaved = on => {
  try {
    window.localStorage.setItem(STAFF_TOOLS_KEY, on ? 'on' : 'off')
  } catch (e) {
    // no storage: the choice lasts this tab (below)
  }
}

// This tab: null follows the saved choice; true or false was set here (a
// staff page, or a flip of the switch).
let session = null
const listeners = new Set()

const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const setSession = value => {
  if (session === value) return
  session = value
  listeners.forEach(listener => listener())
}

// 'on' | 'off' | 'follow' (nothing set in this tab and nothing saved as on)
const snapshot = () => (session === null ? (readSaved() ? 'on' : 'follow') : session ? 'on' : 'off')

// A fresh tab, for tests.
export const resetStaffToolsSession = () => setSession(null)

// allowed: whether this person gets the switch at all (staff, not
// impersonating). Returns { on, toggle }; on is always false when not allowed.
export const useStaffTools = ({ allowed, pathname }) => {
  const state = useSyncExternalStore(subscribe, snapshot, snapshot)
  const staffRoute = isStaffRoute(pathname)

  // On a staff page it is on from the first paint, before the effect below
  // records it for the tab.
  const on = !!allowed && (state === 'on' || (state === 'follow' && staffRoute))

  useEffect(
    () => {
      if (allowed && staffRoute) setSession(true)
    },
    [allowed, staffRoute, pathname]
  )

  const toggle = useCallback(
    () => {
      const next = !on
      writeSaved(next)
      setSession(next)
    },
    [on]
  )

  return { on, toggle }
}
